/**
 * مصادقة محلية لوضع الجهاز الواحد (local mode).
 *
 * ⚠️ نموذج التهديد بشكل صريح:
 *   - هذا الوضع مخصص للاستخدام على جهاز يملكه المستخدم نفسه، أو للتطوير،
 *     أو للعمل دون اتصال. من يملك وصولًا فعليًا للجهاز يملك وصولًا للبيانات،
 *     لأنه لا يوجد خادم يفصل بينهما. هذا مذكور في docs/SECURITY.md.
 *   - كلمات المرور تُخزَّن كتجزئة PBKDF2-SHA256 بملح عشوائي و210,000 تكرار،
 *     ولا تُخزَّن أبدًا كنص صريح.
 *   - في النشر متعدد الشركات يجب استخدام Supabase Auth (انظر docs/DEPLOYMENT.md)،
 *     حيث تُدار الجلسات والتحقق من البريد على الخادم.
 *
 * لا تُستخدم هذه الطبقة لمنح صلاحيات: هي تثبت الهوية فقط. الصلاحيات تأتي
 * من العضويات والأدوار في طبقة السياسات.
 */

import { AppError } from '@/domain/errors';
import { newId } from '@/domain/ids';
import type { AuthGateway, AuthSession, SignInResult, SignUpInput } from '../types';
import type { Profile } from '@/domain/types';
import { getLocalDb, type LocalAccount, type LocalSession } from './db';
import { AUDIT_ACTIONS, severityForAction } from '@/domain/audit';

const PBKDF2_ITERATIONS = 210_000;
const SESSION_HOURS = 12;
export const LOCAL_SESSION_STORAGE_KEY = 'taskflow.local.sessionId';

/* ------------------------------ أدوات التشفير ------------------------------ */

function toBase64(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  for (const byte of view) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function derivePasswordHash(
  password: string,
  salt: Uint8Array,
  iterations: number,
): Promise<string> {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: salt as unknown as BufferSource, iterations, hash: 'SHA-256' },
    keyMaterial,
    256,
  );
  return toBase64(bits);
}

/** مقارنة ثابتة الزمن لمنع هجمات قياس الوقت. */
export function constantTimeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

export async function hashSecret(secret: string): Promise<{ hash: string; salt: string; iterations: number }> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const hash = await derivePasswordHash(secret, salt, PBKDF2_ITERATIONS);
  return { hash, salt: toBase64(salt), iterations: PBKDF2_ITERATIONS };
}

export async function verifySecret(
  secret: string,
  stored: { hash: string; salt: string; iterations: number },
): Promise<boolean> {
  const computed = await derivePasswordHash(secret, fromBase64(stored.salt), stored.iterations);
  return constantTimeEqual(computed, stored.hash);
}

/* --------------------------------- الحسابات -------------------------------- */

export async function findAccountByEmail(email: string): Promise<LocalAccount | null> {
  const db = await getLocalDb();
  const account = (await db.getFromIndex('local_accounts', 'email', email.trim().toLowerCase())) as
    | LocalAccount
    | undefined;
  return account ?? null;
}

export async function createLocalAccount(input: {
  email: string;
  password: string;
  fullName: string;
}): Promise<LocalAccount> {
  const email = input.email.trim().toLowerCase();
  const existing = await findAccountByEmail(email);
  if (existing) throw AppError.conflict('يوجد حساب مسجّل بهذا البريد');
  const secret = await hashSecret(input.password);
  const account: LocalAccount = {
    id: newId(),
    email,
    fullName: input.fullName.trim(),
    passwordHash: secret.hash,
    salt: secret.salt,
    iterations: secret.iterations,
    algorithm: 'PBKDF2-SHA256',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  const db = await getLocalDb();
  await db.put('local_accounts', account);
  return account;
}

export async function updateLocalAccountPassword(accountId: string, password: string): Promise<void> {
  const db = await getLocalDb();
  const account = (await db.get('local_accounts', accountId)) as LocalAccount | undefined;
  if (!account) throw AppError.notFound('الحساب غير موجود');
  const secret = await hashSecret(password);
  await db.put('local_accounts', {
    ...account,
    passwordHash: secret.hash,
    salt: secret.salt,
    iterations: secret.iterations,
    updatedAt: new Date().toISOString(),
  });
}

export async function deleteLocalAccount(accountId: string): Promise<void> {
  const db = await getLocalDb();
  await db.delete('local_accounts', accountId);
}

export async function listLocalAccounts(): Promise<LocalAccount[]> {
  const db = await getLocalDb();
  return (await db.getAll('local_accounts')) as LocalAccount[];
}

/* --------------------------------- الجلسات --------------------------------- */

async function createSession(userId: string, email: string): Promise<LocalSession> {
  const now = new Date();
  const session: LocalSession = {
    id: newId(),
    userId,
    email,
    createdAt: now.toISOString(),
    expiresAt: new Date(now.getTime() + SESSION_HOURS * 3_600_000).toISOString(),
  };
  const db = await getLocalDb();
  await db.put('local_sessions', session);
  persistSessionId(session.id);
  return session;
}

function persistSessionId(id: string | null): void {
  try {
    if (id === null) localStorage.removeItem(LOCAL_SESSION_STORAGE_KEY);
    else localStorage.setItem(LOCAL_SESSION_STORAGE_KEY, id);
  } catch {
    // بيئة بلا localStorage (اختبارات): الجلسة تبقى في الذاكرة فقط
  }
}

function readPersistedSessionId(): string | null {
  try {
    return localStorage.getItem(LOCAL_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

export async function getActiveSession(): Promise<LocalSession | null> {
  const id = readPersistedSessionId();
  if (!id) return null;
  const db = await getLocalDb();
  const session = (await db.get('local_sessions', id)) as LocalSession | undefined;
  if (!session) {
    persistSessionId(null);
    return null;
  }
  if (new Date(session.expiresAt).getTime() <= Date.now()) {
    await db.delete('local_sessions', session.id);
    persistSessionId(null);
    return null;
  }
  // تمديد الجلسة عند النشاط
  const extended: LocalSession = {
    ...session,
    expiresAt: new Date(Date.now() + SESSION_HOURS * 3_600_000).toISOString(),
  };
  await db.put('local_sessions', extended);
  return extended;
}

export async function revokeSession(sessionId: string): Promise<void> {
  const db = await getLocalDb();
  await db.delete('local_sessions', sessionId);
  if (readPersistedSessionId() === sessionId) persistSessionId(null);
}

export async function revokeAllSessionsForUser(userId: string): Promise<number> {
  const db = await getLocalDb();
  const sessions = (await db.getAllFromIndex('local_sessions', 'userId', userId)) as LocalSession[];
  for (const session of sessions) {
    await db.delete('local_sessions', session.id);
  }
  persistSessionId(null);
  return sessions.length;
}

export async function pruneExpiredSessions(): Promise<number> {
  const db = await getLocalDb();
  const sessions = (await db.getAll('local_sessions')) as LocalSession[];
  let removed = 0;
  for (const session of sessions) {
    if (new Date(session.expiresAt).getTime() <= Date.now()) {
      await db.delete('local_sessions', session.id);
      removed += 1;
    }
  }
  return removed;
}

/* ------------------------------- واجهة المصادقة ----------------------------- */

type AuthListener = (session: AuthSession | null) => void;

export class LocalAuthGateway implements AuthGateway {
  private readonly listeners = new Set<AuthListener>();

  async getSession(): Promise<AuthSession | null> {
    const session = await getActiveSession();
    if (!session) return null;
    return { userId: session.userId, email: session.email, expiresAt: session.expiresAt };
  }

  async signIn(email: string, password: string): Promise<SignInResult> {
    const account = await findAccountByEmail(email);
    if (!account) {
      // رسالة موحّدة لتفادي كشف وجود البريد
      throw AppError.unauthenticated('بيانات الدخول غير صحيحة');
    }
    const valid = await verifySecret(password, {
      hash: account.passwordHash,
      salt: account.salt,
      iterations: account.iterations,
    });
    if (!valid) {
      await this.auditFailedLogin(account.id, account.email);
      throw AppError.unauthenticated('بيانات الدخول غير صحيحة');
    }
    const session = await createSession(account.id, account.email);
    const result = { userId: session.userId, email: session.email, requiresEmailConfirmation: false };
    this.emit({ userId: session.userId, email: session.email, expiresAt: session.expiresAt });
    return result;
  }

  async signUp(input: SignUpInput): Promise<SignInResult> {
    const account = await createLocalAccount({
      email: input.email,
      password: input.password,
      fullName: input.fullName,
    });
    // الملف الشخصي يُنشأ مع الحساب (يقابل trigger في Supabase Auth).
    // لا يمنح هذا أي عضوية أو صلاحية؛ الانضمام لشركة يحتاج دعوة/إدارة.
    const now = new Date().toISOString();
    const profile: Profile = {
      id: account.id,
      email: account.email,
      fullName: account.fullName,
      phone: null,
      avatarAttachmentId: null,
      avatarDataUrl: null,
      locale: 'ar',
      status: 'active',
      lastSeenAt: now,
      createdAt: now,
      updatedAt: now,
    };
    const db = await getLocalDb();
    await db.put('profiles', profile as unknown as Record<string, unknown> & { id: string });
    const session = await createSession(account.id, account.email);
    this.emit({ userId: account.id, email: account.email, expiresAt: session.expiresAt });
    return { userId: account.id, email: account.email, requiresEmailConfirmation: false };
  }

  async signOut(): Promise<void> {
    const session = await getActiveSession();
    if (session) await revokeSession(session.id);
    this.emit(null);
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const session = await getActiveSession();
    if (!session) throw AppError.unauthenticated('الجلسة منتهية. سجّل الدخول مجددًا.');
    const db = await getLocalDb();
    const account = (await db.get('local_accounts', session.userId)) as LocalAccount | undefined;
    if (!account) throw AppError.unauthenticated('الحساب غير موجود.');
    const valid = await verifySecret(currentPassword, {
      hash: account.passwordHash,
      salt: account.salt,
      iterations: account.iterations,
    });
    if (!valid) throw AppError.unauthenticated('كلمة المرور الحالية غير صحيحة.');
    await updateLocalAccountPassword(account.id, newPassword);
    // إبطال الجلسات الأخرى على هذا الجهاز مع إبقاء الجلسة الحالية.
    const sessions = (await db.getAllFromIndex(
      'local_sessions',
      'userId',
      account.id,
    )) as LocalSession[];
    for (const other of sessions) {
      if (other.id !== session.id) await db.delete('local_sessions', other.id);
    }
  }

  async requestPasswordReset(email: string): Promise<void> {
    // في الوضع المحلي لا توجد خدمة بريد. لا نخترع إرسالًا وهميًا:
    // نوفّر إعادة تعيين محلية محمية بحساب موجود، ونوثّق القيد.
    const account = await findAccountByEmail(email);
    if (!account) return; // لا نكشف وجود البريد
    throw AppError.unsupported(
      'استعادة كلمة المرور عبر البريد تتطلب ربط خدمة بريد (Supabase Auth). في الوضع المحلي استخدم إعادة التعيين المحلي من شاشة الإعداد.',
    );
  }

  async requestPasswordResetCode(): Promise<void> {
    throw AppError.unsupported(
      'استعادة كلمة المرور بالبريد تتطلب وضع Supabase وخدمة بريد. في الوضع المحلي استخدم إعادة التعيين المحلي.',
    );
  }

  async verifyPasswordResetCode(): Promise<void> {
    throw AppError.unsupported('أكواد الاستعادة غير متاحة في الوضع المحلي.');
  }

  async completePasswordReset(): Promise<void> {
    throw AppError.unsupported('استعادة كلمة المرور بالبريد غير متاحة في الوضع المحلي.');
  }

  async requestEmailChange(): Promise<void> {
    throw AppError.unsupported(
      'تغيير البريد عبر رابط تأكيد يتطلب وضع Supabase وخدمة بريد. في الوضع المحلي أنشئ حسابًا جديدًا أو استخدم أداة المشغّل.',
    );
  }

  onAuthStateChange(callback: AuthListener): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private emit(session: AuthSession | null): void {
    for (const listener of this.listeners) listener(session);
  }

  private async auditFailedLogin(accountId: string, email: string): Promise<void> {
    const db = await getLocalDb();
    await db.put('audit_logs', {
      id: newId(),
      companyId: null,
      actorUserId: accountId,
      actorMembershipId: null,
      action: AUDIT_ACTIONS.unauthorizedAttempt,
      entityType: 'auth',
      entityId: accountId,
      before: null,
      after: { email },
      severity: severityForAction(AUDIT_ACTIONS.unauthorizedAttempt),
      requestId: null,
      createdAt: new Date().toISOString(),
    });
  }
}
